import { createSelector } from '@reduxjs/toolkit';

import { COHORT_BUILDER_SLICE_KEY } from '../constants/constants';
import type { CohortBuilderState } from '../types';
import { selectCohortBuilderState } from './cohortBuilderEditModalSelectors';

/** Cohort Definition Selector */
export const selectCohortDefinition = createSelector(
  selectCohortBuilderState,
  (cohortBuilderState) => cohortBuilderState?.cohortDefinition
);

/** Cohort DSL Selectors */
export const selectFullCohortDsl = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.fullCohortDsl
);

export const selectCohortDsl = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.cohortDsl
);
export const selectIndexDateDsl = createSelector(
  selectCohortDsl,
  (cohortDsl) => cohortDsl?.indexDate
);
export const selectSubjectsVariablesDsl = createSelector(
  selectCohortDsl,
  (cohortDsl) => cohortDsl?.subjectVariables
);
export const selectSubjectVariableDslById = createSelector(
  [selectSubjectsVariablesDsl, (state, id) => id],
  (subjectVariables, id) => subjectVariables?.find((subjectVariable) => subjectVariable.id === id)
);

/** Study Period Restrictions Selectors */
export const selectStudyPeriodRestrictions = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.studyPeriodRestrictions
);

/** Study Period Restriction Selectors */
export const selectOverallStudyPeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.overallStudyPeriodRestriction
);
export const selectOverallStudyPeriodRestrictionIsEnabled = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.isEnabled
);
export const selectOverallStudyPeriodRestrictionStartDate = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.startDate
);
export const selectOverallStudyPeriodRestrictionEndDate = createSelector(
  selectOverallStudyPeriodRestriction,
  (overallStudyPeriodRestriction) => overallStudyPeriodRestriction?.endDate
);

/** Baseline Period Restriction Selectors */
export const selectBaselinePeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.baselinePeriodRestriction
);
export const selectBaselinePeriodRestrictionIsEnabled = createSelector(
  selectBaselinePeriodRestriction,
  (baselinePeriodRestriction) => baselinePeriodRestriction?.isEnabled
);
export const selectBaselinePeriodRestrictionDaysBeforeIndexDate = createSelector(
  selectBaselinePeriodRestriction,
  (baselinePeriodRestriction) => baselinePeriodRestriction?.daysBeforeIndexDate
);

/** Follow Up Period Restriction Selectors */
export const selectFollowUpPeriodRestriction = createSelector(
  selectStudyPeriodRestrictions,
  (studyPeriodRestrictions) => studyPeriodRestrictions?.followUpPeriodRestriction
);
export const selectFollowUpPeriodRestrictionIsEnabled = createSelector(
  selectFollowUpPeriodRestriction,
  (followUpPeriodRestriction) => followUpPeriodRestriction?.isEnabled
);
export const selectFollowUpPeriodRestrictionDaysAfterIndexDate = createSelector(
  selectFollowUpPeriodRestriction,
  (followUpPeriodRestriction) => followUpPeriodRestriction?.daysAfterIndexDate
);

/** Index Date Selectors */
export const selectIndexDate = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.indexDate
);
export const selectIndexDateId = createSelector(selectIndexDate, (indexDate) => indexDate?.id);
export const selectIndexDateType = createSelector(selectIndexDate, (indexDate) => indexDate?.type);
export const selectIndexDateValue = createSelector(
  selectIndexDate,
  (indexDate) => indexDate?.indexDateValue
);

/** Subject Variables Selectors */
export const selectSubjectVariables = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.subjectVariables
);

export const selectSubjectVariableById = createSelector(
  [selectSubjectVariables, (state, id?: string) => id],
  (subjectVariables, id) => {
    return subjectVariables?.find((subjectVariable) => subjectVariable.id === id);
  }
);

/** Simple Events Selectors */
export const selectSimpleEvents = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.simpleEvents
);

/** Derived Events Selectors */
export const selectDerivedEvents = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.derivedEvents
);

export const selectIndexDateRange = createSelector(
  selectCohortDefinition,
  (cohortDefinition) => cohortDefinition?.indexDate?.indexDateRangeValue
);

export const selectCohortUpdatedByBuilder = (state: CohortBuilderState) => {
  return state?.[COHORT_BUILDER_SLICE_KEY].isCohortUpdatedByBuilder;
};
